<template>
  <section class="terrain-gps-capture" aria-live="polite">
    <LocationField
      v-if="showOriginal"
      label="Ubicación inicial"
      :capture="gpsOriginal"
      @retry="captureOriginal"
    />

    <LocationField
      v-if="showFinal"
      label="Ubicación antes del envío"
      :capture="gpsFinal"
      @retry="captureFinal"
    />

    <LocationField
      v-if="mode === 'location'"
      :label="label || 'Ubicación'"
      :capture="locationCapture"
      @retry="captureLocation"
    />

    <p v-if="isCapturing" class="terrain-gps-capture-status">
      Obteniendo ubicación del dispositivo…
    </p>
    <div v-if="errorMessage" class="terrain-gps-capture-error" role="alert">
      {{ errorMessage }}
    </div>

    <button
      v-if="mode === 'location'"
      type="button"
      class="terrain-gps-capture-button"
      :disabled="isCapturing"
      @click="captureLocation"
    >
      {{ locationCapture ? 'Actualizar ubicación' : 'Capturar ubicación' }}
    </button>
  </section>
</template>

<script>
import LocationField from './LocationField.vue';

const GEOLOCATION_OPTIONS = Object.freeze({
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
});

const GEOLOCATION_GUIDANCE = 'No fue posible obtener la ubicación. Habilite el permiso de ubicación del navegador y del dispositivo, luego vuelva a intentarlo.';

// Punto de partida cuando el dispositivo/navegador nunca entrega un fix real
// (permiso denegado, origen sin HTTPS, sin hardware GPS): el mismo centro por
// defecto que usa el mapa principal (ver mapService.js), para que el
// Respondente pueda ubicar el marcador a mano en vez de quedar bloqueado.
const MANUAL_FALLBACK_COORDINATES = [-70.4, -33.27];

const MANUAL_FALLBACK_MESSAGE = 'No fue posible obtener la ubicación automática. Se ubicó un punto de referencia: ajústelo manualmente en el mapa.';

/**
 * Builds a GPSCaptureDTO for when no real device fix is available. Manual:
 * true tells the UI to show a "Manual" badge instead of an accuracy
 * classification (there is no real Precisión_GPS to report).
 */
export function gpsCaptureManualFallback(capturedAt = new Date().toISOString()) {
  return {
    captured: {
      type: 'Point',
      coordinates: [...MANUAL_FALLBACK_COORDINATES],
      accuracyMeters: 0,
      capturedAt,
      manual: true,
    },
  };
}

/**
 * Converts the browser GeolocationPosition into the GPSCaptureDTO consumed by
 * the Terrain API. Browser coordinates are latitude/longitude, while Terrain
 * stores GeoJSON points as [longitude, latitude].
 */
export function gpsCaptureFromPosition(position, capturedAt = new Date().toISOString()) {
  const { latitude, longitude, accuracy } = position.coords;
  if (
    !Number.isFinite(longitude) || longitude < -180 || longitude > 180 ||
    !Number.isFinite(latitude) || latitude < -90 || latitude > 90 ||
    !Number.isFinite(accuracy) || accuracy < 0
  ) {
    throw new Error('El dispositivo entregó una posición GPS inválida.');
  }

  return {
    captured: {
      type: 'Point',
      coordinates: [longitude, latitude],
      accuracyMeters: accuracy,
      capturedAt,
    },
  };
}

export default {
  name: 'GpsCapture',
  components: { LocationField },
  props: {
    // response captures GPS_Original on mount and exposes a fresh GPS_Final
    // capture through captureFinal/captureForSubmission. location captures a
    // single answer for a location-type question on user request.
    mode: {
      type: String,
      default: 'response',
      validator: (value) => ['response', 'location'].includes(value),
    },
    label: {
      type: String,
      default: '',
    },
    autoCaptureOriginal: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      gpsOriginal: null,
      gpsFinal: null,
      locationCapture: null,
      isCapturing: false,
      errorMessage: '',
    };
  },
  computed: {
    showOriginal() {
      return this.mode === 'response' && Boolean(this.gpsOriginal || this.isCapturing || this.errorMessage);
    },
    showFinal() {
      return this.mode === 'response' && (this.gpsFinal || this.errorMessage);
    },
    canSubmit() {
      return this.mode !== 'response' || Boolean(this.gpsOriginal && this.gpsFinal);
    },
  },
  mounted() {
    if (this.mode === 'response' && this.autoCaptureOriginal) {
      this.captureOriginal();
    }
  },
  methods: {
    requestPosition() {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        return Promise.reject(new Error(GEOLOCATION_GUIDANCE));
      }

      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, GEOLOCATION_OPTIONS);
      });
    },
    applyCapture(kind, capture) {
      const target = kind === 'location' ? 'locationCapture' : kind;
      this[target] = capture;
      this.$emit('captured', { kind, capture });
      this.$emit(`update:${kind}`, capture);
    },
    async capture(kind) {
      this.isCapturing = true;
      this.errorMessage = '';
      try {
        const position = await this.requestPosition();
        const capture = gpsCaptureFromPosition(position);
        this.applyCapture(kind, capture);
        return capture;
      } catch (error) {
        // El dispositivo/navegador no entregó una posición real: en vez de
        // dejar al Respondente bloqueado, se aplica un punto de partida
        // manual (ver gpsCaptureManualFallback) que el Respondente ajusta
        // a mano en el mapa.
        this.errorMessage = MANUAL_FALLBACK_MESSAGE;
        this.$emit('capture-error', { kind, message: this.errorMessage });
        const fallback = gpsCaptureManualFallback();
        this.applyCapture(kind, fallback);
        return fallback;
      } finally {
        this.isCapturing = false;
      }
    },
    captureOriginal() {
      return this.capture('gpsOriginal');
    },
    captureFinal() {
      return this.capture('gpsFinal');
    },
    captureLocation() {
      return this.capture('location');
    },
    /**
     * Call this immediately before the parent submits the response. It always
     * requests a fresh position, so GPS_Final cannot reuse GPS_Original.
     */
    async captureForSubmission() {
      // A response is only eligible for submission when both independently
      // captured values exist. Do not request GPS_Final after a failed initial
      // capture: the caller must keep the submit action blocked until the user
      // re-enables location and retries GPS_Original.
      if (!this.gpsOriginal) {
        this.errorMessage = GEOLOCATION_GUIDANCE;
        this.$emit('submission-blocked', { message: this.errorMessage });
        return null;
      }

      try {
        return await this.captureFinal();
      } catch (error) {
        this.$emit('submission-blocked', { message: this.errorMessage });
        return null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-gps-capture {
  display: flex;
  flex-direction: column;
  gap: var(--terrain-space-sm);
}

.terrain-gps-capture-status {
  color: var(--color-ghost);
  margin: 0;
}

.terrain-gps-capture-error {
  border: 1px solid var(--color-danger);
  border-radius: var(--terrain-radius-md);
  background: var(--terrain-color-danger-light);
  color: var(--color-danger);
  padding: var(--terrain-space-sm) var(--terrain-space-md);
}

.terrain-gps-capture-button {
  align-self: flex-start;
  border: 1px solid var(--color-navy);
  border-radius: var(--terrain-radius-xl);
  background: var(--color-navy);
  color: var(--color-white);
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  cursor: pointer;
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-steel); border-color: var(--color-steel); }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
}
</style>
